/**
 * booking-step-2.js
 * -------------------
 * Page controller for Booking Step 2 ("Complete Your Event").
 *
 * Follows the shared lifecycle from booking-lifecycle.js:
 *   Load Booking State -> Validate/Render -> Handle interaction ->
 *   Save Booking State -> Navigate.
 *
 * Data sources, and why:
 *   - Package summary (name/price) comes from the cached `booking.package`
 *     object in Booking State — NOT from any catalog owned by this page.
 *     That cache is written atomically by Step 1 (see
 *     Booking.setPrimaryService), so Step 2 never has to know anything
 *     about Step 1's blocks, products, or DOM.
 *   - Recommended service / add-on cards come from THIS section's own
 *     catalog (see the `[data-booking-catalog]` block emitted above),
 *     generated straight from this section's own Theme Editor blocks.
 *   - "Which services are already selected" comes from `booking.services`
 *     (real customer session) or, only when that's empty, the
 *     `data-preview-selected-services` fallback meant for Theme Editor
 *     previews with no live session.
 *
 * Booking State write rule (fixes a data-loss bug in the old code, which
 * wiped `booking.services` down to just whatever was checked on this
 * page — silently deleting the primary package/service Step 1 had
 * already stored): this page only ever APPENDS newly chosen recommended
 * services to the existing array, and only fully replaces `addons`
 * (which is exclusively owned by this step).
 *
 * Checkout: Continue resolves the Shopify variant for the selected
 * package from the cached `booking.package.variantId` (written by Step 1
 * via Booking.setPrimaryService — this page never touches Step 1's
 * catalog), adds it to the cart via the Cart AJAX API with all booking
 * details attached as line item properties, and only redirects to
 * /checkout once that request has actually succeeded.
 */
(function (window) {
  'use strict';

  var BookingStep2 = {
    init: function (root) {
      if (!root) return;

      var catalog = window.BookingCatalog.read(root);
      var normalizeKey = window.BookingCatalog.normalizeKey;
      var formatMoney = window.BookingCatalog.formatMoney;

      var panelTotalEl = root.querySelector('[data-panel-total]');
      var footerTotalEl = root.querySelector('[data-footer-total]');
      var extrasBlock = root.querySelector('[data-extras-block]');
      var packageNameEl = root.querySelector('[data-package-name]');
      var packagePriceEl = root.querySelector('[data-package-price]');

      var packagePrice = 0;

      // ---- Which services are already selected (Booking State, else --
      //      Theme Editor preview fallback) ------------------------------
      function getSelectedServiceKeys(state) {
        if (state && Array.isArray(state.services) && state.services.length) {
          return state.services.map(function (s) { return normalizeKey(s.id); });
        }

        var preview = root.dataset.previewSelectedServices || '';
        return preview.split(',').map(normalizeKey).filter(Boolean);
      }

      // ---- Package summary: cached `booking.package`, else preview -----
      function renderPackageSummary(state) {
        var pkg = state && state.package && state.package.key ? state.package : null;

       if (pkg) {
    packagePrice = Number(pkg.advancePrice) || 0;
    if (packageNameEl) packageNameEl.textContent = pkg.name || '';
} else {
          packagePrice = Number(root.dataset.previewPackagePrice) || 0;
          if (packageNameEl) packageNameEl.textContent = root.dataset.previewPackageName || '';
        }

        if (packagePriceEl) packagePriceEl.textContent = formatMoney(packagePrice);
      }

      // ---- Hide already-selected service cards, show group labels -----
      function applyServiceFiltering(state) {
        var selected = getSelectedServiceKeys(state);

        root.querySelectorAll('[data-group-item="service"]').forEach(function (card) {
          if (selected.indexOf(card.dataset.serviceKey) !== -1) {
            card.remove();
          }
        });

        var remainingServices = root.querySelectorAll('[data-group-item="service"]').length;
        var remainingAddons = root.querySelectorAll('[data-group-item="addon"]').length;
        var totalRemaining = remainingServices + remainingAddons;

        var serviceLabel = root.querySelector('[data-group-label="service"]');
        var addonLabel = root.querySelector('[data-group-label="addon"]');

        if (totalRemaining >= 4) {
          if (serviceLabel && remainingServices > 0) serviceLabel.hidden = false;
          if (addonLabel && remainingAddons > 0) addonLabel.hidden = false;
        }
        if (remainingServices === 0 && serviceLabel) serviceLabel.hidden = true;
      }

      // ---- Steppers / running total ------------------------------------
      function getExtrasState() {
        var state = [];
        root.querySelectorAll('[data-extra]').forEach(function (card) {
          var qty = parseInt(card.querySelector('[data-qty]').textContent, 10) || 0;
          if (qty <= 0) return;
          var entry = catalog[card.dataset.id];
          state.push({
            id: card.dataset.id,
            groupItem: card.dataset.groupItem,
            serviceKey: card.dataset.serviceKey || (entry && entry.serviceKey) || '',
            name: (entry && entry.name) || card.dataset.name,
            price: entry ? Number(entry.price) || 0 : parseFloat(card.dataset.price) || 0,
            qty: qty
          });
        });
        return state;
      }

      function render() {
        var extras = getExtrasState();
        var extrasTotal = extras.reduce(function (sum, i) {
    return sum + (i.price * i.qty);
}, 0);

var total = packagePrice + extrasTotal;

        if (extras.length === 0) {
          extrasBlock.innerHTML = '';
        } else {
          var rows = '<div class="nw-srow head"><span>Add-ons</span></div>';
          extras.forEach(function (i) {
            var label = i.name + (i.qty > 1 ? ' x' + i.qty : '');
            rows += '<div class="nw-srow"><span>' + label + '</span><b>' + formatMoney(i.price * i.qty) + '</b></div>';
          });
          extrasBlock.innerHTML = rows;
        }

        panelTotalEl.textContent = formatMoney(total);
footerTotalEl.textContent = formatMoney(total);
      }

      function wireSteppers() {
        root.querySelectorAll('[data-extra]').forEach(function (card) {
          var addBtn = card.querySelector('[data-add]');
          var stepper = card.querySelector('[data-stepper]');
          var qtyEl = card.querySelector('[data-qty]');
          var incBtn = card.querySelector('[data-increment]');
          var decBtn = card.querySelector('[data-decrement]');

          function setQty(qty) {
            qty = Math.max(0, qty);
            qtyEl.textContent = qty;
            if (qty > 0) {
              addBtn.hidden = true;
              stepper.hidden = false;
            } else {
              addBtn.hidden = false;
              stepper.hidden = true;
            }
            render();
          }

          addBtn.addEventListener('click', function () { setQty(1); });
          incBtn.addEventListener('click', function () { setQty(parseInt(qtyEl.textContent, 10) + 1); });
          decBtn.addEventListener('click', function () { setQty(parseInt(qtyEl.textContent, 10) - 1); });
        });
      }

      function wireSummaryToggle() {
        var toggle = root.querySelector('[data-summary-toggle]');
        var panel = root.querySelector('[data-summary-panel]');
        var chev = root.querySelector('[data-chev]');
        toggle.addEventListener('click', function () {
          panel.classList.toggle('open');
          chev.classList.toggle('open');
        });
      }

      // ---- Add to cart + redirect to checkout --------------------------
      function buildLineItemProperties(state, addons, additionalServices) {
        var event = state.event || {};
        var props = {};

        function set(label, value) {
          if (value === undefined || value === null || value === '') return;
          props[label] = String(value);
        }

        set('Event Type', event.eventType);
        set('Event Date', event.eventDate);
        set('Event Time', event.eventTime);
        set('City', event.city);
        set('Guests', event.guests);
        set('Venue', event.venue);
        set('Landmark', event.landmark);
        set('Notes', event.notes);
        set('Package', state.package && state.package.name);

        if (addons.length) {
          set('Add-ons', addons.map(function (a) {
            return a.name + (a.qty > 1 ? ' x' + a.qty : '');
          }).join(', '));
        }

        if (additionalServices.length) {
          set('Additional Services', additionalServices.map(function (s) {
            return s.id;
          }).join(', '));
        }

        // Hidden from the customer-facing cart/checkout (Shopify hides any
        // property whose key starts with "_"), kept for order processing.
        props._booking_state = JSON.stringify(state);

        return props;
      }

      function showContinueError(message) {
        var errorEl = root.querySelector('[data-continue-error]');
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
      }

      function clearContinueError() {
        var errorEl = root.querySelector('[data-continue-error]');
        if (errorEl) errorEl.hidden = true;
      }

      function setContinueLoading(continueBtn, isLoading) {
        var label = continueBtn.querySelector('[data-continue-label]');
        continueBtn.disabled = isLoading;
        if (label) label.textContent = isLoading ? 'Adding to cart…' : '';
        if (label && !isLoading) {
          label.innerHTML = 'Continue <i class="ti ti-arrow-right"></i>';
        }
      }

      function wireContinue() {
        var continueBtn = root.querySelector('.nw-continue');

        continueBtn.addEventListener('click', function () {
          clearContinueError();

          var current = window.Booking.load();
          var pkg = current.package;

          if (!pkg || !pkg.key) {
            showContinueError('Please choose a package before continuing.');
            return;
          }

          if (!pkg.variantId) {
            showContinueError('This package isn\'t available to book online right now. Please contact us.');
            return;
          }

          var existingServices = Array.isArray(current.services) ? current.services : [];
          var newlyChosenServices = [];

          getExtrasState().forEach(function (extra) {
            if (extra.groupItem !== 'service') return;
            var alreadyPresent = existingServices.some(function (s) {
              return normalizeKey(s.id) === normalizeKey(extra.serviceKey);
            });
            if (!alreadyPresent) {
              var entry = { id: extra.serviceKey, package: 'default' };
              existingServices.push(entry);
              newlyChosenServices.push(entry);
            }
          });

          var addons = getExtrasState()
            .filter(function (extra) { return extra.groupItem === 'addon'; })
            .map(function (extra) {
              return { id: extra.id, name: extra.name, price: extra.price, qty: extra.qty };
            });

          var saved = window.Booking.save({ services: existingServices, addons: addons });
          var properties = buildLineItemProperties(saved, addons, newlyChosenServices);

          setContinueLoading(continueBtn, true);

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              items: [{ id: pkg.variantId, quantity: 1, properties: properties }]
            })
          })
            .then(function (response) {
              return response.json().then(function (data) {
                if (!response.ok) {
                  throw new Error(data && data.description ? data.description : 'Could not add this booking to your cart.');
                }
                return data;
              });
            })
            .then(function () {
              window.Booking.navigate('/checkout');
            })
            .catch(function (err) {
              setContinueLoading(continueBtn, false);
              showContinueError(err && err.message ? err.message : 'Something went wrong adding this booking to your cart. Please try again.');
            });
        });
      }

      // ---- Lifecycle ----------------------------------------------------
      wireSteppers();
      wireSummaryToggle();
      wireContinue();

      window.BookingLifecycle.run({
        root: root,
        render: function (state) {
          renderPackageSummary(state);
          applyServiceFiltering(state);
          render();
        }
      });
    }
  };

  window.BookingStep2 = BookingStep2;
})(window);